import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "./LoadingSpinner";
import "../Styles/AprovechaTusBeneficios.css";

// Funciones de validación
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhone = (phone) => {
  // Validar formato específico: +56997858386 (código país +56, 9, y 8 dígitos)
  const phoneRegex = /^\+569[0-9]{8}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

const validateRUT = (rut) => {
  // Si está vacío, no es válido (campo requerido)
  if (!rut.trim()) return false;
  
  // Validación de RUT chileno
  if (/^[0-9]{7,8}-[0-9kK]$/.test(rut)) {
    const rutLimpio = rut.replace(/\./g, '').replace(/-/g, '');
    const dv = rutLimpio.slice(-1);
    const numero = rutLimpio.slice(0, -1);
    
    let suma = 0;
    let multiplicador = 2;
    
    for (let i = numero.length - 1; i >= 0; i--) {
      suma += parseInt(numero[i]) * multiplicador;
      multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
    }
    
    const dvEsperado = 11 - (suma % 11);
    const dvCalculado = dvEsperado === 11 ? '0' : dvEsperado === 10 ? 'K' : dvEsperado.toString();
    
    return dv.toUpperCase() === dvCalculado;
  }
  
  // Validación de pasaporte (formato básico: letras y números, mínimo 6 caracteres)
  const pasaporteRegex = /^[A-Za-z0-9]{6,}$/;
  return pasaporteRegex.test(rut);
};

const validateName = (name) => {
  // Solo letras, espacios y algunos caracteres especiales comunes en nombres
  const nameRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/;
  return nameRegex.test(name) && name.trim().length >= 2;
};

const AprovechaTusBeneficios = () => {
  const dispatch = useDispatch();
  const { fetching, data, error } = useSelector((state) => state.SendNotificationEmail);
  
  const [showForm, setShowForm] = useState(false);
  const [notificationType, setNotificationType] = useState('email');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    rutOrPassport: '',
    email: '',
    phone: ''
  });

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    rutOrPassport: '',
    email: '',
    phone: ''
  });

  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    rutOrPassport: false,
    email: false,
    phone: false
  });

  const handleClick = () => {
    setShowForm((prev) => !prev);
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'firstName':
      case 'lastName':
        if (!value.trim()) return 'Este campo es requerido';
        if (!validateName(value)) return 'Solo se permiten letras y espacios';
        if (value.trim().length < 2) return 'Mínimo 2 caracteres';
        return '';
      
      case 'rutOrPassport':
        if (!value.trim()) return 'Este campo es requerido';
        if (!validateRUT(value)) return 'RUT o pasaporte inválido';
        return '';
      
      case 'email':
        if (!value.trim()) return 'Este campo es requerido';
        if (!validateEmail(value)) return 'Email inválido';
        return '';
      
      case 'phone':
        if (value.trim() && !validatePhone(value)) {
          return 'Teléfono inválido (formato: +56912345678)';
        }
        return '';
      
      default:
        return '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let formattedValue = value;
    
    if (name === 'rutOrPassport') {
      if (/^[0-9kK]+$/.test(value.replace(/\./g, '').replace(/-/g, ''))) {
        const rutLimpio = value.replace(/\./g, '').replace(/-/g, '').replace(/[^0-9kK]/g, '');
        if (rutLimpio.length <= 8) {
          formattedValue = rutLimpio;
        } else if (rutLimpio.length === 9) {
          formattedValue = rutLimpio.slice(0, -1) + '-' + rutLimpio.slice(-1);
        }
      } else {
        formattedValue = value.replace(/[^A-Za-z0-9]/g, '');
      }
    } else if (name === 'phone') {
      const phoneLimpio = value.replace(/\D/g, '');
      if (!value.startsWith('+')) {
        if (phoneLimpio.length === 0) {
          formattedValue = '+';
        } else if (phoneLimpio.length <= 2) {
          formattedValue = '+' + phoneLimpio;
        } else if (phoneLimpio.length <= 4) {
          formattedValue = '+' + phoneLimpio.slice(0, 2) + phoneLimpio.slice(2);
        } else if (phoneLimpio.length <= 11) {
          formattedValue = '+' + phoneLimpio.slice(0, 2) + phoneLimpio.slice(2);
        } else {
          formattedValue = '+' + phoneLimpio.slice(0, 11);
        }
      } else {
        if (phoneLimpio.length <= 11) {
          formattedValue = '+' + phoneLimpio;
        } else {
          formattedValue = '+' + phoneLimpio.slice(0, 11);
        }
      }
    } else if (name === 'firstName' || name === 'lastName') {
      formattedValue = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '');
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));

    if (touched[name]) {
      const error = validateField(name, formattedValue);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    Object.keys(formData).forEach(field => {
      const error = validateField(field, formData[field]);
      newErrors[field] = error;
      if (error) isValid = false;
    });
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched({
      firstName: true,
      lastName: true,
      rutOrPassport: true,
      email: true,
      phone: true
    });
    if (!validateForm()) {
      return;
    }
    const payload = {
      ...formData,
      notificationType
    };
    dispatch({ type: "SEND_NOTIFICATION_EMAIL_REQUEST", payload });
  };

  useEffect(() => {
    if (data && !fetching) {
      setFormData({
        firstName: '',
        lastName: '',
        rutOrPassport: '',
        email: '',
        phone: ''
      });
      setNotificationType('email');
      setErrors({
        firstName: '',
        lastName: '',
        rutOrPassport: '',
        email: '',
        phone: ''
      });
      setTouched({
        firstName: false,
        lastName: false,
        rutOrPassport: false,
        email: false,
        phone: false
      });
    }
  }, [data, fetching]);

  return (
    <div className="aprovecha-beneficios-container">
      <div className="aprovecha-beneficios-content-vertical">
        <button 
          className="aprovecha-beneficios-btn"
          onClick={handleClick}
        >
          Aprovecha tus Beneficios
        </button>
        <div className={`aprovecha-beneficios-form-accordion${showForm ? ' open' : ''} mt-4`}> 
          <form className="aprovecha-beneficios-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Nombre *</label>
                <input 
                  type="text" 
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  placeholder="Ingresa tu nombre" 
                  required 
                  disabled={fetching}
                  className={touched.firstName && errors.firstName ? 'error-input' : ''}
                />
                {touched.firstName && errors.firstName && (
                  <span className="field-error">{errors.firstName}</span>
                )}
              </div>
              <div className="form-group">
                <label>Apellido *</label>
                <input 
                  type="text" 
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  placeholder="Ingresa tu apellido" 
                  required 
                  disabled={fetching}
                  className={touched.lastName && errors.lastName ? 'error-input' : ''}
                />
                {touched.lastName && errors.lastName && (
                  <span className="field-error">{errors.lastName}</span>
                )}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group full-width">
                <label>RUT o pasaporte *</label>
                <input 
                  type="text" 
                  name="rutOrPassport"
                  value={formData.rutOrPassport}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  placeholder="RUT: 1234567-9 o Pasaporte: ABC123456" 
                  required 
                  disabled={fetching}
                  className={touched.rutOrPassport && errors.rutOrPassport ? 'error-input' : ''}
                />
                {touched.rutOrPassport && errors.rutOrPassport && (
                  <span className="field-error">{errors.rutOrPassport}</span>
                )}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  placeholder="tu@email.com" 
                  required 
                  disabled={fetching}
                  className={touched.email && errors.email ? 'error-input' : ''}
                />
                {touched.email && errors.email && (
                  <span className="field-error">{errors.email}</span>
                )}
              </div>
              <div className="form-group">
                <label>Telefono</label>
                <input 
                  type="text" 
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  placeholder="+56912345678" 
                  disabled={fetching}
                  className={touched.phone && errors.phone ? 'error-input' : ''}
                />
                {touched.phone && errors.phone && (
                  <span className="field-error">{errors.phone}</span>
                )}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group full-width">
                <label>Medio de contacto *</label>
                <div className="contact-method-options">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="notificationType"
                      value="email"
                      checked={notificationType === 'email'}
                      onChange={() => setNotificationType('email')}
                      required
                      disabled={fetching}
                    />
                    Email
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="notificationType"
                      value="telefono"
                      checked={notificationType === 'telefono'}
                      onChange={() => setNotificationType('telefono')}
                      required
                      disabled={fetching}
                    />
                    Teléfono
                  </label>
                </div>
              </div>
            </div>
            <div className="form-row">
              <button 
                type="submit" 
                className="aprovecha-beneficios-submit"
                disabled={fetching}
              >
                {fetching ? (
                  <>
                    <LoadingSpinner size="small" />
                    <span style={{ marginLeft: '8px' }}>ENVIANDO...</span>
                  </>
                ) : (
                  'ENVIAR MENSAJE'
                )}
              </button>
            </div>
            {error && (
              <div className="form-row">
                <div className="error-message">
                  Error al enviar el mensaje: {error}
                </div>
              </div>
            )}
            {data && !fetching && (
              <div className="form-row">
                <div className="success-message">
                  Mensaje enviado exitosamente
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AprovechaTusBeneficios; 